'use client';

import { useId, useState } from 'react';
import { CaretDown, Check } from '@/components/icons';
import type { SelectOption } from './select';
import { useAnchored } from './use-anchored';
import { usePopover } from './use-popover';
import styles from './multi-select.module.css';
import { useT } from '@/lib/locale-context';

/**
 * MultiSelect - the design-system multi-choice dropdown: pick several options
 * from a closed set. Same trigger chrome, popover anchoring and keyboard model
 * as `Select` (the CSS module composes the Select's classes; the behaviour
 * mirrors its handlers), with the multi-specific differences: choosing an
 * option TOGGLES it and keeps the panel open, the listbox is
 * `aria-multiselectable`, and a reserved leading check slot marks selection.
 * Controlled: `values` in, `onChange(values)` out, values normalised to the
 * options' declared order.
 */
export function MultiSelect({
  options,
  values,
  onChange,
  ariaLabel,
  placeholder,
  size = 'md',
  disabled,
  align = 'start',
  className,
  testId,
}: {
  options: SelectOption[];
  /** The selected values (controlled). */
  values: string[];
  onChange: (values: string[]) => void;
  ariaLabel?: string;
  /** Trigger text when nothing is selected. */
  placeholder?: string;
  size?: 'sm' | 'md';
  disabled?: boolean;
  align?: 'start' | 'end';
  /** Layout hook only. */
  className?: string;
  testId?: string;
}) {
  const t = useT();
  const { open, setOpen, ref: wrapRef } = usePopover<HTMLDivElement>();
  const { anchorRef, floatingRef } = useAnchored<HTMLButtonElement, HTMLDivElement>(open, {
    align,
    matchWidth: true,
  });
  const [active, setActive] = useState(-1);
  const listboxId = useId();
  const selected = options.filter((o) => values.includes(o.value));
  const optionId = (i: number) => `${listboxId}-opt-${i}`;

  function openMenu() {
    if (disabled) return;
    const i = options.findIndex((o) => values.includes(o.value) && !o.disabled);
    setActive(i >= 0 ? i : step(options, -1, 1));
    setOpen(true);
  }
  function toggle(opt: SelectOption) {
    if (opt.disabled) return;
    const has = values.includes(opt.value);
    // Normalise to the options' declared order so the trigger text and the
    // caller's array agree regardless of click order.
    const next = options
      .filter((o) => (o.value === opt.value ? !has : values.includes(o.value)))
      .map((o) => o.value);
    onChange(next);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (disabled) return;
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
        e.preventDefault();
        openMenu();
      }
      return;
    }
    switch (e.key) {
      case 'Escape':
        setOpen(false);
        anchorRef.current?.focus();
        break;
      case 'Tab':
        // Close and let focus move on naturally.
        setOpen(false);
        break;
      case 'ArrowDown':
        e.preventDefault();
        setActive((i) => step(options, i, 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActive((i) => step(options, i, -1));
        break;
      case 'Home':
        e.preventDefault();
        setActive(step(options, -1, 1));
        break;
      case 'End':
        e.preventDefault();
        setActive(step(options, options.length, -1));
        break;
      case 'Enter':
      case ' ': {
        e.preventDefault();
        const o = options[active];
        if (o) toggle(o);
        break;
      }
    }
  }

  return (
    <div
      className={className ? `${styles.wrap} ${className}` : styles.wrap}
      ref={wrapRef}
      data-testid={testId}
    >
      <button
        ref={anchorRef}
        type="button"
        className={styles.trigger}
        data-size={size}
        data-open={open || undefined}
        data-empty={selected.length === 0 || undefined}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        aria-controls={open ? listboxId : undefined}
        aria-activedescendant={open && active >= 0 ? optionId(active) : undefined}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openMenu())}
        onKeyDown={onKeyDown}
      >
        <span className={styles.value}>
          {selected.length > 0
            ? selected.map((o) => o.label).join(', ')
            : (placeholder ?? t('kit.multiSelect.none'))}
        </span>
        <CaretDown className={styles.caret} aria-hidden />
      </button>

      {open && (
        <div
          ref={floatingRef}
          id={listboxId}
          className={styles.menu}
          role="listbox"
          aria-multiselectable="true"
          aria-label={ariaLabel}
        >
          {options.map((o, i) => {
            const isSelected = values.includes(o.value);
            return (
              <button
                key={o.value}
                id={optionId(i)}
                type="button"
                role="option"
                aria-selected={isSelected}
                className={styles.option}
                data-active={i === active || undefined}
                disabled={o.disabled}
                onMouseEnter={() => setActive(i)}
                // Keep focus on the trigger so the keyboard keeps working
                // between mouse toggles (the panel stays open).
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => toggle(o)}
              >
                <span className={styles.checkSlot} aria-hidden>
                  {isSelected && <Check className={styles.check} />}
                </span>
                <span className={styles.optLabel}>{o.label}</span>
                {o.hint && <span className={styles.optHint}>{o.hint}</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Next enabled option index from `from` in direction `dir`, wrapping.
 *  (Mirror of the private helper in select.tsx; keep the two in step.) */
function step(options: SelectOption[], from: number, dir: 1 | -1): number {
  let i = from;
  for (let n = 0; n < options.length; n++) {
    i = (i + dir + options.length) % options.length;
    if (!options[i]!.disabled) return i;
  }
  return Math.max(0, from);
}
